import { FC } from 'react';

import { GiftSellerSidebar } from '@service/giftSeller/containers/GiftSellerSidebar/GiftSellerSidebar';
import { GiftChoiceUnitFormPage } from '@service/giftSeller/page/GiftChoiceUnitFormPage';
import { GiftChoiceUnitListPage } from '@service/giftSeller/page/GiftChoiceUnitListPage';
import { GiftAnalysisHubPage } from '@service/giftSeller/page/GiftAnalysisHubPage';
import { GiftBrandManagePage } from '@service/giftSeller/page/GiftBrandManagePage';
import { GiftCsMemoLogPage } from '@service/giftSeller/page/GiftCsMemoLogPage';
import { GiftChannelLedgerPage } from '@service/giftSeller/page/GiftChannelLedgerPage';
import { GiftDeliveryCategoryFormPage } from '@service/giftSeller/page/GiftDeliveryCategoryFormPage';
import { GiftDeliveryManagementPage } from '@service/giftSeller/page/GiftDeliveryManagementPage';
import { GiftItemBrandFormPage } from '@service/giftSeller/page/GiftItemBrandFormPage';
import { GiftItemBulkFormPage } from '@service/giftSeller/page/GiftItemBulkFormPage';
import { GiftItemFormPage } from '@service/giftSeller/page/GiftItemFormPage';
import { GiftItemInfoFormPage } from '@service/giftSeller/page/GiftItemInfoFormPage';
import { GiftItemInfoListPage } from '@service/giftSeller/page/GiftItemInfoListPage';
import { GiftItemListPage } from '@service/giftSeller/page/GiftItemListPage';
import { GiftSearchPage } from '@service/giftSeller/page/GiftSearchPage';
import { GiftSendManagePage } from '@service/giftSeller/page/GiftSendManagePage';
import { GiftOrderDashboardPage } from '@service/giftSeller/page/GiftOrderDashboardPage';
import { GiftOrderListPage } from '@service/giftSeller/page/GiftOrderListPage';
import { GiftPhoneOrderPage } from '@service/giftSeller/page/GiftPhoneOrderPage';
import { GiftSellerSettingsPage } from '@service/giftSeller/page/GiftSellerSettingsPage';
import { GiftSettlementAgencyPage } from '@service/giftSeller/page/GiftSettlementAgencyPage';
import { GiftSettlementSupplyPage } from '@service/giftSeller/page/GiftSettlementSupplyPage';
import { IssuedManagementPage } from '@service/giftSeller/page/IssuedManagementPage';
import { useAuth } from '@shared/state/hooks/useAuth';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

/**
 * 탭으로 묶인 페이지의 구 경로 → 새 경로 ?tab= 리다이렉트 (북마크·타 페이지 링크 보존).
 * tab 외 쿼리(?date= 딥링크 등)는 그대로 넘긴다.
 */
const RedirectToTab: FC<{ to: string; tab?: string }> = ({ to, tab }) => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  if (tab) params.set('tab', tab);
  else params.delete('tab');
  const qs = params.toString();
  return <Navigate to={qs ? `${to}?${qs}` : to} replace />;
};

const GiftSellerAuthRouter: FC = () => {
  const { isGiftAgent, isAdmin } = useAuth();

  const isSellerAuthenticated = isGiftAgent() || isAdmin();

  if (isSellerAuthenticated) {
    sessionStorage.removeItem('redirectUrlAfterGiftSellerLogin');
    return (
      <>
        <GiftSellerSidebar />
        <div className="min-h-screen lg:pl-64">
          <div className="flex flex-1 flex-col">
            <Routes>
              <Route path="" element={<GiftOrderListPage />} />
              <Route path="home" element={<GiftOrderListPage />} />
              <Route path="dashboard" element={<GiftOrderDashboardPage />} />
              {/* 검색 — 쿠폰 내역 + 주문 탭 */}
              <Route path="search" element={<GiftSearchPage />} />
              <Route path="coupon-history" element={<RedirectToTab to="/gift-seller/search" />} />
              <Route
                path="order-search"
                element={<RedirectToTab to="/gift-seller/search" tab="order" />}
              />
              {/* 전화주문 관리 (주문 생성=상주검색 탭 + 입금 확인 탭) */}
              <Route path="phone-order" element={<GiftPhoneOrderPage />} />
              {/* 구 상주검색 경로 — 전화주문 관리의 주문 생성 탭으로 통합 (북마크 보존) */}
              <Route
                path="member-search"
                element={<Navigate to="/gift-seller/phone-order" replace />}
              />
              {/* 환불/연장 관리 */}
              <Route path="issued-management" element={<IssuedManagementPage />} />
              {/* 발송 관리 — 발송 대기 + 발송내역 조회 탭 */}
              <Route path="send" element={<GiftSendManagePage />} />
              <Route
                path="coupon-redemption"
                element={<RedirectToTab to="/gift-seller/send" />}
              />
              <Route
                path="delivered-history"
                element={<RedirectToTab to="/gift-seller/send" tab="history" />}
              />
              {/* CS 응대 로그 — Admin·GiftAgent 공용 (관리자 전용 메모는 서버가 걸러 내려줌) */}
              <Route path="cs-memo-log" element={<GiftCsMemoLogPage />} />
              {/* 매체 충전금 미러 원장 (잔액 대사·수동 기장) */}
              <Route path="channel-ledger" element={<GiftChannelLedgerPage />} />
              {/* GiftDelivery (배송 관리) */}
              <Route
                path="delivery-management"
                element={<GiftDeliveryManagementPage />}
              />
              {/* 브랜드 관리 — 상품 브랜드 + 배송 카테고리 + 배송관리자 계정 탭 (관리자 전용).
                  폼 저장 후 navigate는 구 목록 경로로 오므로 탭으로 넘긴다(북마크 보존). */}
              <Route path="brand" element={<GiftBrandManagePage />} />
              <Route path="item-brand" element={<RedirectToTab to="/gift-seller/brand" />} />
              <Route path="item-brand/new" element={<GiftItemBrandFormPage />} />
              <Route
                path="item-brand/:brandId/edit"
                element={<GiftItemBrandFormPage />}
              />
              <Route
                path="brand-seller-user"
                element={<RedirectToTab to="/gift-seller/brand" tab="seller" />}
              />
              <Route
                path="delivery-category"
                element={<RedirectToTab to="/gift-seller/brand" tab="category" />}
              />
              <Route
                path="delivery-category/new"
                element={<GiftDeliveryCategoryFormPage />}
              />
              <Route
                path="delivery-category/:categoryId/edit"
                element={<GiftDeliveryCategoryFormPage />}
              />
              {/* GiftChoiceUnit (선택유닛) */}
              <Route path="choice-unit" element={<GiftChoiceUnitListPage />} />
              <Route path="choice-unit/new" element={<GiftChoiceUnitFormPage />} />
              <Route
                path="choice-unit/:unitId/edit"
                element={<GiftChoiceUnitFormPage />}
              />
              {/* GiftItem (브랜드별 판매상품) */}
              <Route path="item" element={<GiftItemListPage />} />
              <Route path="item/new" element={<GiftItemFormPage />} />
              <Route path="item/bulk" element={<GiftItemBulkFormPage />} />
              <Route path="item/:itemId/edit" element={<GiftItemFormPage />} />
              {/* GiftItemInfo (원상품) */}
              <Route path="item-info" element={<GiftItemInfoListPage />} />
              <Route path="item-info/new" element={<GiftItemInfoFormPage />} />
              <Route
                path="item-info/:itemInfoId/edit"
                element={<GiftItemInfoFormPage />}
              />
              {/* 데이터 분석 + 이벤트 로그 탭 */}
              <Route path="analysis" element={<GiftAnalysisHubPage />} />
              <Route
                path="event-log"
                element={<RedirectToTab to="/gift-seller/analysis" tab="event-log" />}
              />
              {/* 정산 관리 — 배송업체(supply) / 상조 배송형(rebate) / 상조 쿠폰형(coupon) 페이지 분리 */}
              <Route
                path="settlement"
                element={<Navigate to="/gift-seller/settlement/supply" replace />}
              />
              <Route path="settlement/supply" element={<GiftSettlementSupplyPage />} />
              {/* 상조 정산은 배송형·쿠폰형을 한 페이지(월별 합계 + 종류별 탭)로 통합.
                  기존 종류별 경로는 해당 탭으로 넘긴다(북마크 보존). */}
              <Route path="settlement/agency" element={<GiftSettlementAgencyPage />} />
              <Route
                path="settlement/agency/delivery"
                element={
                  <Navigate to="/gift-seller/settlement/agency?tab=delivery" replace />
                }
              />
              <Route
                path="settlement/agency/coupon"
                element={
                  <Navigate to="/gift-seller/settlement/agency?tab=coupon" replace />
                }
              />
              {/* 계정정보 */}
              <Route path="settings" element={<GiftSellerSettingsPage />} />
              <Route path="*" element={<Navigate to="/gift-seller/home" />} />
            </Routes>
          </div>
        </div>
      </>
    );
  } else {
    return <Navigate to="/gift-seller/login" />;
  }
};

export default GiftSellerAuthRouter;
